import { Box, TextField, Grid, Stack } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ToWords } from 'to-words';
import {
  IClientInvoice,
  InvoiceExtra,
  InvoiceExtraType,
  ITaxTypesDetails,
  getOurEntityTaxationList,
  getManualTaxation,
  OurEntityTaxation,
  ClientDTO,
  IOurEntity,
  ClosureDTO,
  IClientCurrencySettings,
  InvoiceExtraCharge,
} from 'wisestep-api';
import { ControlledTextInput, Label } from 'wisestep-base';

import WsSwitch from '../../../components/WsSwitch';
import { AutocompleteWithPersistentAdd } from '../../clientDetails/components/contractArrangementsTab/AddEditFeeArrangement';
import { numToWords, currencyToWords } from '../helper/InvoiceNumberToWord';

export interface IInvoiceAmountForm {
  invoiceTaxFields: ITaxTypesDetails[];
  invoiceTaxSuggestionData: string[];
  additionalTaxFields: ITaxTypesDetails[];
  additionalTaxSuggestionData: string[];
  selectedInvoiceTaxFields: ITaxTypesDetails[];
  setSelectedInvoiceTaxFields: Dispatch<SetStateAction<ITaxTypesDetails[]>>;
  selectedAdditionalTaxFields: ITaxTypesDetails[];
  setSelectedAdditionalTaxFields: Dispatch<SetStateAction<ITaxTypesDetails[]>>;
  currentInvoice?: IClientInvoice;
  setOpenInvoiceTaxDialog: (openInvoiceTaxDialog) => void;
  setOpenAdditionalTaxDialog: (openAdditionalTaxDialog) => void;
  invoiceExtras: InvoiceExtra[];
  isDraft: boolean;
  client?: ClientDTO;
  ourEntity?: IOurEntity;
  invoiceCurrency?: IClientCurrencySettings;
}

export interface ClientInvoiceExtraField extends InvoiceExtraCharge {
  absolute: boolean;
  percent: number;
}

export interface InvoiceCandidateField {
  closure?: ClosureDTO;
}

interface FormValues {
  closures: InvoiceCandidateField[];
  extras: ClientInvoiceExtraField[];
  subTotal: number;
  totalBeforeTaxation: number;
  adjustment: number;
  total: number;
  taxation: string;
  taxations: ITaxTypesDetails[];
  adjustments: ITaxTypesDetails[];
  amountInWord: string;
  discountPercent: number;
  ourEntityId: string;
  id?: string;
}

const InvoiceAmountForm = ({
  invoiceTaxFields,
  additionalTaxFields,
  selectedInvoiceTaxFields,
  selectedAdditionalTaxFields,
  setOpenInvoiceTaxDialog,
  setOpenAdditionalTaxDialog,
  invoiceExtras,
  isDraft,
  client,
  ourEntity,
  invoiceCurrency,
}: IInvoiceAmountForm) => {
  const methods = useFormContext<FormValues>();
  const { getValues, setValue, watch } = methods;
  const subTotal = watch('subTotal');
  const ourEntityId = watch('ourEntityId');
  const edit = !!getValues().id;
  const [discountedTotal, setDiscountedTotal] = useState(0);
  const [ourEntityTaxationList, setOurEntityTaxationList] = useState<OurEntityTaxation[]>([]);
  const [manualTaxation, setManualTaxation] = useState<boolean>();

  const getInvoiceComputationExtras = () =>
    getValues('extras').filter((extra) => extra.invoiceExtra?.behavior !== 'DESCRIPTION');

  const updateTotal = () => {
    let discAmnt = 0;
    getInvoiceComputationExtras().forEach((extra) => {
      const invoiceExtra = getInvoiceExtraById(extra.invoiceExtra?.id);
      const amount = extra.amount ?? 0;
      if (invoiceExtra.type === InvoiceExtraType.ADDITIVE) {
        discAmnt += amount;
      } else {
        discAmnt -= amount;
      }
    });
    let total = 0;
    if (subTotal) {
      total = subTotal + discAmnt;
    }
    setDiscountedTotal(total);
    setValue('totalBeforeTaxation', total);
    const discountParcent = (discAmnt / subTotal) * 100;
    setValue('discountPercent', parseInt(discountParcent + ''));

    if (!manualTaxation) {
      let taxations: ITaxTypesDetails[] = [];
      let adjustments: ITaxTypesDetails[] = [];
      if (
        client?.location?.country &&
        ourEntity?.Country &&
        ourEntity.Country === client.location.country
      ) {
        switch (ourEntity.Country) {
          case 'India': {
            const oetGST = ourEntityTaxationList
              .filter((t) => t.location?.country === 'India')
              .find((t) => t.taxType.name === 'GST');
            if (oetGST) {
              if (oetGST.location?.state === client.location.state) {
                taxations = ['CGST', 'SGST'].map(
                  (tax) =>
                    ({ name: tax, rate: +oetGST.taxType.rate / 2 } as unknown as ITaxTypesDetails)
                );
              } else if (oetGST.location?.state !== client.location.state) {
                taxations = [{ name: 'IGST', rate: oetGST.taxType.rate } as ITaxTypesDetails];
              }
            }
            break;
          }
          case 'United States': {
            const taxes = ourEntityTaxationList
              .filter(
                (oet) =>
                  oet.location?.country === 'United States' &&
                  oet.location?.state === client.location?.state
              )
              .map((t) => t.taxType);
            taxations = taxes.filter((t) => t.applyTo === 'Invoice');
            adjustments = taxes.filter((t) => t.applyTo === 'Taxes');
            break;
          }
          default: {
            const sameCountryTaxes = ourEntityTaxationList
              .filter((oet) => oet.location?.country === client.location?.country)
              .map((oet) => oet.taxType);
            taxations = sameCountryTaxes.filter((t) => t.applyTo === 'Invoice');
            adjustments = sameCountryTaxes.filter((t) => t.applyTo === 'Taxes');
            break;
          }
        }
      }
      setValue('taxations', taxations);
      setValue('adjustments', adjustments);
    }

    if (!edit) {
      const taxations = getValues('taxations') ?? [];
      const adjustments = getValues('adjustments') ?? [];
      setValue(
        'taxation',
        ((taxations.reduce((acc, tax) => +tax.rate + acc, 0) * total) / 100).toFixed(2)
      );
      setValue(
        'adjustment',
        +(
          (total *
            adjustments.reduce(
              (acc, t) =>
                t.applyToTaxes
                  ?.filter((id) => taxations.some((taxation) => taxation.id === id))
                  ?.map((id) => taxations.find((taxation) => taxation.id === id))
                  ?.reduce(
                    (inneracc, parentTax) => +(parentTax?.rate ?? 0) * +t.rate + inneracc,
                    0
                  ) ?? 0 + acc,
              0
            )) /
          10000
        ).toFixed(2)
      );
    }
  };

  useEffect(() => {
    updateTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTotal, client, ourEntity, manualTaxation, ourEntityTaxationList]);

  useEffect(() => {
    if (edit) {
      return;
    }
    let vatPercentage = 0;
    selectedInvoiceTaxFields.map(
      (invoiceTax) => (vatPercentage = vatPercentage + parseInt(invoiceTax.rate))
    );
    const taxation = (vatPercentage * discountedTotal) / 100;
    const total = discountedTotal + parseInt(taxation + '');
    const additionalTax = !isNaN(getValues('adjustment')) ? +getValues('adjustment') : 0;

    setValue('total', +total + additionalTax);
    setValue('amountInWord', numToWords(total + additionalTax));
  }, [selectedInvoiceTaxFields, discountedTotal, edit, getValues, setValue]);

  useEffect(() => {
    getOurEntityTaxationList(ourEntityId).then((response) =>
      setOurEntityTaxationList(response.data)
    );
  }, [ourEntityId]);

  useEffect(() => {
    getManualTaxation().then((response) => setManualTaxation(response.data.manual));
  }, []);

  useEffect(() => {
    if (edit) {
      return;
    }
    let totalAdditionalTax = 0;
    selectedAdditionalTaxFields.map((invoiceTax) => {
      invoiceTax.applyToTaxes?.forEach((applyToTax) => {
        const foundInvoiceTax = selectedInvoiceTaxFields.find(
          (invoiceTax) => invoiceTax.id == applyToTax
        );
        if (foundInvoiceTax?.rate) {
          const invoiceTaxAmount = (parseInt(foundInvoiceTax.rate) * discountedTotal) / 100;
          totalAdditionalTax =
            totalAdditionalTax + (parseInt(invoiceTax.rate) * invoiceTaxAmount) / 100;
        }
      });
    });
    const total = discountedTotal + getValues('adjustment');
    const taxation = !isNaN(+getValues('taxation')) ? +getValues('taxation') : 0;
    setValue('total', +(total + taxation).toFixed(2));
    const toWords = new ToWords({
      localeCode: 'en-US',
      converterOptions: {
        currency: true,
        ignoreDecimal: false,
        ignoreZeroCurrency: false,
        doNotAddOnly: false,
        currencyOptions: currencyToWords[invoiceCurrency?.symbol ?? 'USD'] ?? {},
      },
    });
    const amount = total + taxation || 0;
    setValue('amountInWord', toWords.convert(amount));
  }, [
    selectedAdditionalTaxFields,
    discountedTotal,
    selectedInvoiceTaxFields,
    edit,
    getValues,
    setValue,
  ]);

  const getInvoiceExtraById = (id?: string): InvoiceExtra =>
    invoiceExtras?.find((invoiceExtra) => invoiceExtra.id === id) ?? ({} as InvoiceExtra);

  return (
    <>
      <Grid md={12} container item p={2} mt={3}>
        <Grid md={3} justifyContent="left" display="flex">
          <Label>Sub Total</Label>
        </Grid>
        <Grid md={6}></Grid>
        <Grid md={3} justifyContent="left" display="flex">
          <ControlledTextInput
            name="subTotal"
            type="text"
            fullWidth
            placeholder="Sub Total"
            variant="outlined"
            disabled={true}
          />
        </Grid>
      </Grid>

      <Grid xs={12} container item p={2} rowSpacing={2}>
        {getValues('extras').map(
          (extra, index) =>
            extra.invoiceExtra?.behavior !== 'DESCRIPTION' && (
              <>
                <Grid item xs={3}>
                  <Label>{getInvoiceExtraById(extra.invoiceExtra?.id).name}</Label>
                </Grid>
                <Grid item xs={2}>
                  {!(isDraft ? false : edit) && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Label> %</Label>
                      <WsSwitch name={`extras.${index}.absolute`} />
                      <Label>$</Label>
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={3.5} pr={1}>
                  {!(isDraft ? false : edit) && !getValues(`extras.${index}.absolute`) && (
                    <TextField
                      fullWidth
                      onChange={(e) => {
                        const inputPercentage = +e.target.value;
                        const subTotal = getValues('subTotal') ?? 0;
                        const discountvar = (inputPercentage * subTotal) / 100;
                        setValue(`extras.${index}.percent`, inputPercentage);
                        setValue(`extras.${index}.amount`, parseInt(discountvar + ''));
                        updateTotal();
                      }}
                      inputProps={{ style: { padding: '11.5px 14px' } }}
                    />
                  )}
                </Grid>
                <Grid item xs={3.5} pl={3.5}>
                  <ControlledTextInput
                    name={`extras.${index}.amount`}
                    disabled={isDraft ? false : edit}
                    fullWidth
                    type="number"
                    placeholder="Discount"
                    variant="outlined"
                    changed={() => updateTotal()}
                  />
                </Grid>
              </>
            )
        )}
      </Grid>

      <Grid md={12} container item p={2}>
        <Grid md={3} justifyContent="left" display="flex">
          <Label>Total Before Taxation</Label>
        </Grid>
        <Grid md={6}></Grid>
        <Grid md={3} justifyContent="left" display="flex">
          <ControlledTextInput
            name="totalBeforeTaxation"
            type="text"
            fullWidth
            placeholder="Total Before Taxation"
            variant="outlined"
            disabled={true}
          />
        </Grid>
      </Grid>

      <Grid md={12} container item p={2}>
        <Grid md={3} justifyContent="left" display="flex">
          <Label>Invoice Taxation</Label>
        </Grid>
        <Grid md={6}>
          {(!edit || isDraft) && (
            <Box pr={4}>
              <AutocompleteWithPersistentAdd
                options={invoiceTaxFields}
                placeholder="Invoice Taxation"
                disabled={!manualTaxation}
                getOptionLabel={(t) => `${t.name}-${t.rate}%`}
                name="taxations"
                creatable
                onCreate={() => setOpenInvoiceTaxDialog(true)}
                multiple
                changed={() => updateTotal()}
              />
            </Box>
          )}
        </Grid>
        <Grid md={3}>
          <ControlledTextInput
            name="taxation"
            fullWidth
            placeholder="Taxation"
            variant="outlined"
            disabled={true}
          />
        </Grid>
      </Grid>

      <Grid md={12} container item p={2}>
        <Grid md={3} justifyContent="left" display="flex">
          <Label>Additional Taxation</Label>
        </Grid>
        <Grid md={6}>
          {(!edit || isDraft) && (
            <Box pr={4}>
              <AutocompleteWithPersistentAdd
                getOptionLabel={(t) => `${t.name}-${t.rate}%`}
                options={additionalTaxFields}
                placeholder="Additional Taxation"
                disabled={!manualTaxation}
                name="adjustments"
                creatable
                onCreate={() => setOpenAdditionalTaxDialog(true)}
                multiple
                changed={() => updateTotal()}
              />
            </Box>
          )}
        </Grid>
        <Grid md={3}>
          <ControlledTextInput
            name="adjustment"
            fullWidth
            placeholder="Additional Taxation"
            variant="outlined"
            disabled={true}
          />
        </Grid>
      </Grid>
      <Grid md={12} container item p={2}>
        <Grid md={3} justifyContent="left" display="flex">
          <Label>Total</Label>
        </Grid>
        <Grid md={6}></Grid>
        <Grid md={3} justifyContent="left" display="flex">
          <ControlledTextInput
            name="total"
            type="text"
            disabled={true}
            fullWidth
            placeholder="Total"
            variant="outlined"
          />
        </Grid>
      </Grid>
    </>
  );
};

export default InvoiceAmountForm;
